import { CommonModule, NgOptimizedImage } from '@angular/common';
// logo-parrain.component.ts
import {
    Component,
    DestroyRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    computed,
    effect,
    inject,
    signal,
} from '@angular/core';
import { FuseConfig, FuseConfigService } from '@fuse/services/config';
import Swiper, { Autoplay, EffectFade, SwiperOptions } from 'swiper';
import { SwiperComponent, SwiperModule } from 'swiper/angular';

import Parrain from 'config/parrain';
import { ParrainStore } from 'app/modules/admin/stores/parrain.store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

Swiper.use([Autoplay, EffectFade]);

export type LogoContext =
    | 'footer'
    | 'sidebar'
    | 'header'
    | 'banner'
    | 'advertising'
    | 'default';

@Component({
    selector: 'app-logo-parrain',
    standalone: true,
    imports: [CommonModule, NgOptimizedImage, SwiperModule],
    templateUrl: './logo-parrain.component.html',
    styleUrls: ['./logo-parrain.component.scss'],
})
export class LogoParrainComponent implements OnInit {
    @Input() totalCycleMinutes = 60; // 1h pour un tour complet
    @Input() showTimer = false; // pour afficher mm:ss jusqu’au prochain switch
    @ViewChild(SwiperComponent) swiperCmp?: SwiperComponent;
    private _tickIntervalId: any = null; // pour le compte à rebours
    private _firstTimeoutId: any = null; // premier alignement
    private _rotationIntervalId: any = null; // rotations suivantes

    // timer affichage
    readonly remainingMs = signal(0);
    readonly remainingLabel = computed(() => {
        const ms = this.remainingMs();
        const s = Math.floor(ms / 1000);
        const mm = String(Math.floor(s / 60)).padStart(2, '0');
        const ss = String(s % 60).padStart(2, '0');
        return `${mm}:${ss}`;
    });
    private _fillImage = signal(false);
    @Input() set fillImage(v: boolean) {
        this._fillImage.set(!!v);
    }

    private _fuseConfigService = inject(FuseConfigService);
    private _parrainStore = inject(ParrainStore);
    private _destroyRef = inject(DestroyRef);

    config!: FuseConfig;

    // Signaux pour les propriétés d'entrée
    private _parrainId = signal<string | null>(null);
    private _useMainSponsor = signal(true);
    private _context = signal<LogoContext>('default');
    private _customClass = signal<string>('');

    // Dimensions
    private _width = signal<number>(100);
    private _height = signal<number>(50);
    @Output() parrainClicked = new EventEmitter<string>();
    @Input() set parrainId(value: string | null) {
        this._parrainId.set(value);
    }

    @Input() set useMainSponsor(value: boolean) {
        this._useMainSponsor.set(value);
    }

    @Input() set context(value: LogoContext) {
        this._context.set(value);
        this._updateDimensions(value);
    }

    @Input() set customClass(value: string) {
        this._customClass.set(value);
    }

    constructor() {
        // Observer les changements dans le store pour rafraîchir le swiper
        effect(() => {
            const sponsors = this.mainSponsors();
            const shouldShow = this.shouldShowSwiper();

            // Rafraîchir le swiper quand le nombre de sponsors change
            if (sponsors.length > 0) {
                setTimeout(() => this._refreshSwiper(), 100);
            }
        });
           // relancer la mécanique quand la liste change
        effect(() => {
            const _ = this.mainSponsors(); // trigger
            // petite latence pour que le Swiper soit prêt
            setTimeout(() => this._setupClockAlignedRotation(), 50);
        });
    }

    private _slotDurationMs(): number {
        return 12 * 1000; // 12 secondes en s
    }

    private _currentIndexByClock(): number {
        const count = Math.max(1, this.mainSponsors().length || 1);
        const slot = this._slotDurationMs(); // 12 min
        const withinHour = Date.now() % (60 * 60 * 1000);
        return Math.floor(withinHour / slot) % count;
    }

    private _msUntilNextBoundary(): number {
        const slot = this._slotDurationMs(); // 12 min
        const withinHour = Date.now() % (60 * 60 * 1000);
        const rem = slot - (withinHour % slot);
        return rem === slot ? 0 : rem;
    }

    // Configuration Swiper simplifiée
    readonly swiperConfig: SwiperOptions = {
        slidesPerView: 1,
        navigation: false,
        pagination: false,
        scrollbar: false,
        loop: true,
        speed: 1000,
        centeredSlides: true,
        allowTouchMove: true,
        spaceBetween: 0,
        effect: 'fade',
        fadeEffect: { crossFade: true },
    };

    // Computed values
    readonly currentContext = computed(() => this._context());
    readonly additionalClass = computed(() => this._customClass());
    readonly imageWidth = computed(() => this._width());
    readonly imageHeight = computed(() => this._height());

    // Classes CSS Tailwind pour le conteneur selon le contexte
    readonly containerTailwindClasses = computed(() => {
        const context = this._context();
        const baseClasses = 'flex items-center justify-center';

        switch (context) {
            case 'footer':
                return `${baseClasses} w-16 h-12`; // Taille compacte pour footer
            case 'sidebar':
                return `${baseClasses} w-20 h-20`;
            case 'advertising':
                return `${baseClasses} w-40 h-24`;
            case 'banner':
                return `${baseClasses} w-20 h-12`;
            case 'header':
                return `${baseClasses} w-18 h-12`;
            default:
                return `${baseClasses} w-18 h-auto`;
        }
    });

    // Récupérer tous les sponsors principaux avec ordre stable
    readonly mainSponsors = computed<Parrain[]>(() => {
        const all = this._parrainStore.getAllParrains();
        return all
            .filter((p) => p.isMainSponsor)
            .slice(0, 5)
            .sort((a, b) => {
                const ao = (a as any).order ?? 0;
                const bo = (b as any).order ?? 0;
                if (ao !== bo) return ao - bo;
                return (a.id || a.name || '').localeCompare(
                    b.id || b.name || ''
                );
            });
    });

    // Déterminer si on doit afficher le swiper avec effet de bord géré
    readonly shouldShowSwiper = computed(() => {
        const useMain = this._useMainSponsor();
        const sponsorsLength = this.mainSponsors().length;
        const shouldShow = useMain && sponsorsLength > 1;

        return shouldShow;
    });

    // Computed pour forcer la re-évaluation quand les données changent
    readonly isLoading = computed(() => this._parrainStore.getLoading());
    readonly hasError = computed(() => this._parrainStore.getError());

    // Signal pour forcer le re-render du swiper
    private _swiperKey = signal(0);
    readonly swiperKey = computed(() => this._swiperKey());

    // Méthode pour forcer la mise à jour du swiper
    private _refreshSwiper(): void {
        this._swiperKey.update((key) => key + 1);
    }

    // Parrain unique (quand il n'y a qu'un seul sponsor principal ou un ID spécifique)
    readonly parrain = computed<Parrain | undefined>(() => {
        // Si un ID spécifique est fourni, l'utiliser en priorité
        const id = this._parrainId();
        if (id) {
            return this._parrainStore.getParrainById(id);
        }

        // Si useMainSponsor est true et qu'il n'y a qu'un seul sponsor principal
        if (this._useMainSponsor()) {
            const mainSponsors = this.mainSponsors();
            if (mainSponsors.length === 1) {
                return mainSponsors[0];
            }

            // Si aucun sponsor principal trouvé en BDD, utiliser le fallback de la config
            if (mainSponsors.length === 0) {
                const fallback =
                    this._parrainStore.getFallbackParrainFromConfig();
                if (fallback) {
                    return fallback;
                }
            }
        }

        // En dernier recours, utiliser le parrain de la configuration Fuse
        if (this.config?.parrain) {
            return this._convertFuseParrain(this.config.parrain);
        }

        return undefined;
    });

    readonly logoSrc = computed(() => {
        const p = this.parrain();
        if (!p) return '';
        return this._buildLogoUrl(p);
    });

    readonly logoClass = computed(() => {
        const p = this.parrain();
        if (!p) return 'w-18 h-auto object-contain';
        return this._buildLogoClass(p);
    });

    readonly containerClass = computed(() => {
        const p = this.parrain();
        if (!p) return '';
        return this._buildContainerClass(p);
    });

    ngOnInit(): void {
        this._getConfig();
        this._updateDimensions(this._context());
        if (this._useMainSponsor()) this._parrainStore.loadMainSponsors();

     
    }

    ngOnDestroy(): void {
        this._clearTimers();
    }

    onSwiper(swiper: Swiper) {
        // quand le Swiper est prêt
        this._setupClockAlignedRotation();
    }

    private _setupClockAlignedRotation(): void {
        if (!this.shouldShowSwiper()) {
            this._clearTimers();
            return;
        }
        const swiper = this.swiperCmp?.swiperRef;
        if (!swiper) return;

        // 1) désactiver l’autoplay interne
        swiper.params.autoplay = false;
        swiper.autoplay?.stop?.();

        // 2) positionner le slide courant selon l’heure
        const idx = this._currentIndexByClock();
        swiper.slideToLoop(idx, 0);

        // 3) timers
        this._clearTimers();

        const slot = this._slotDurationMs();
        const firstDelay = this._msUntilNextBoundary();

        // compte à rebours
        this.remainingMs.set(firstDelay);
        this._tickIntervalId = setInterval(() => {
            const next = this.remainingMs() - 1000;
            this.remainingMs.set(next > 0 ? next : 0);
        }, 1000);

        // premier passage pile au prochain “bord”
        this._firstTimeoutId = setTimeout(() => {
            swiper.slideNext(1000);
            this.remainingMs.set(slot);
            // boucle suivante toutes les slots
            this._rotationIntervalId = setInterval(() => {
                swiper.slideNext(1000);
                this.remainingMs.set(slot);
            }, slot);
        }, firstDelay);
    }

    private _clearTimers(): void {
        if (this._tickIntervalId) {
            clearInterval(this._tickIntervalId);
            this._tickIntervalId = null;
        }
        if (this._firstTimeoutId) {
            clearTimeout(this._firstTimeoutId);
            this._firstTimeoutId = null;
        }
        if (this._rotationIntervalId) {
            clearInterval(this._rotationIntervalId);
            this._rotationIntervalId = null;
        }
    }

    // Méthode pour construire l'URL du logo - adaptée aux deux structures
    buildLogoUrl(parrain: Parrain): string {
        return this._buildLogoUrl(parrain);
    }

    // Méthode pour construire les classes du logo
    buildLogoClass(parrain: Parrain): string {
        return this._buildLogoClass(parrain);
    }

    // Méthode pour construire les classes du conteneur
    buildContainerClass(parrain: Parrain): string {
        return this._buildContainerClass(parrain);
    }

    private _buildLogoUrl(parrain: Parrain): string {
        // 1. Vérifier si c'est un parrain de la DB avec logo_sponsor (structure Partenaire)
        if (
            (parrain as any).logo_sponsor &&
            typeof (parrain as any).logo_sponsor === 'string'
        ) {
            const logoUrl = (parrain as any).logo_sponsor;

            return logoUrl;
        }

        // 2. Vérifier si logo.url est une URL complète
        if (parrain.logo?.url && parrain.logo.url.startsWith('http')) {
            return parrain.logo.url;
        }

        // 3. Construire l'URL à partir des composants (structure config)
        if (parrain.logo?.url && !parrain.logo.url.startsWith('http')) {
            const constructedUrl = `${parrain.logo.url}${
                parrain.logo.urlExt ? '-' + parrain.logo.urlExt : ''
            }`;
            return constructedUrl;
        }
        return 'assets/images/logo/kappsi-logo.png';
    }

    private _buildLogoClass(parrain: Parrain): string {
        const context = this._context();
        let widthClass = 'w-[60%]';
        let heightClass = 'h-auto';
        let additionalClasses = 'object-contain rounded-md';

        // Sélectionner les bonnes classes selon le contexte
        switch (context) {
            case 'footer':
                widthClass =
                    parrain.styles?.logo?.size?.bottomBarRight
                        ?.classnameWidth || 'w-12';
                heightClass =
                    parrain.styles?.logo?.size?.bottomBarRight
                        ?.classnameHeight || 'h-auto';
                // Classes spéciales pour la qualité dans le footer
                additionalClasses =
                    'object-contain rounded-sm antialiased filter-none';
                break;
            case 'sidebar':
                widthClass =
                    parrain.styles?.logo?.size?.sidebarLeftBottom
                        ?.classnameWidth || 'w-20';
                heightClass =
                    parrain.styles?.logo?.size?.sidebarLeftBottom
                        ?.classnameHeight || 'h-auto';
                additionalClasses = 'object-contain rounded-md antialiased';
                break;
            case 'advertising':
                widthClass =
                    parrain.styles?.logo?.size?.advertising?.classnameWidth ||
                    'w-28';
                heightClass =
                    parrain.styles?.logo?.size?.advertising?.classnameHeight ||
                    'h-auto';
                additionalClasses = 'object-contain rounded-lg antialiased';
                break;
            case 'banner':
                widthClass =
                    parrain.styles?.logo?.size?.bannerFeed?.classnameWidth ||
                    'w-16';
                heightClass =
                    parrain.styles?.logo?.size?.bannerFeed?.classnameHeight ||
                    'h-auto';
                additionalClasses = 'object-contain rounded-md antialiased';
                break;
            case 'header':
                widthClass = 'w-16';
                heightClass = 'h-auto';
                additionalClasses = 'object-contain rounded-md antialiased';
                break;
            default:
                widthClass = 'w-18';
                heightClass = 'h-auto';
                additionalClasses = 'object-contain rounded-md antialiased';
        }

        // Intégration de la classe personnalisée
        const customClass = this._customClass();
        if (this._fillImage()) {
            widthClass = 'w-full';
            heightClass = 'h-full';
            additionalClasses = 'object-cover rounded-md'; // couvre tout le conteneur
        }
        return `${widthClass} ${heightClass} ${additionalClasses} ${customClass}`.trim();
    }

    private _buildContainerClass(parrain: Parrain): string {
        const context = this._context();
        const classes = [];

        // Classes selon le contexte
        switch (context) {
            case 'footer':
                // Footer : minimal mais avec padding visible
                if (parrain.customize?.needBackground && !this._fillImage()) {
                    classes.push(
                        parrain.customize.backgroundColor || 'bg-white'
                    );
                    classes.push('rounded-sm');
                }
                classes.push('p-1'); // 🎯 Padding plus important pour footer
                break;

            case 'sidebar':
                // Sidebar : background complet
                if (parrain.customize?.needBackground && !this._fillImage()) {
                    classes.push(
                        parrain.customize.backgroundColor || 'bg-white'
                    );
                    classes.push('rounded-md');
                }
                classes.push('p-3'); // 🎯 Padding plus important pour sidebar
                break;

            case 'advertising':
                // Advertising : style premium avec plus de padding
                if (parrain.customize?.needBackground && !this._fillImage()) {
                    classes.push(
                        parrain.customize.backgroundColor || 'bg-white'
                    );
                    classes.push('rounded-lg');
                }
                classes.push('p-4 shadow-sm'); // 🎯 Padding encore plus important pour advertising
                break;

            case 'banner':
                // Banner : style intermédiaire
                if (parrain.customize?.needBackground && !this._fillImage()) {
                    classes.push(
                        parrain.customize.backgroundColor || 'bg-white'
                    );
                    classes.push('rounded-md');
                }
                classes.push('p-2'); // 🎯 Padding plus important pour banner
                break;

            case 'header':
                // Header : style clean avec padding
                if (parrain.customize?.needBackground && !this._fillImage()) {
                    classes.push(
                        parrain.customize.backgroundColor || 'bg-white'
                    );
                    classes.push('rounded-md');
                }
                classes.push('p-2'); // 🎯 Padding plus important pour header
                break;

            default:
                // Default : style standard
                if (parrain.customize?.needBackground && !this._fillImage()) {
                    classes.push(
                        parrain.customize.backgroundColor || 'bg-white'
                    );
                    classes.push('rounded-md');
                }
                classes.push('p-2'); // 🎯 Padding par défaut augmenté
        }

        return classes.join(' ');
    }

    private _getConfig() {
        this._fuseConfigService.config$
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((config: FuseConfig) => {
                this.config = config;
            });
    }

    private _updateDimensions(context: LogoContext): void {
        // Définir des dimensions appropriées pour chaque contexte
        switch (context) {
            case 'sidebar':
                this._width.set(96); // w-24
                this._height.set(64); // h-16
                break;
            case 'footer':
                this._width.set(64); // w-16
                this._height.set(40); // h-10
                break;
            case 'advertising':
                this._width.set(160); // w-32
                this._height.set(96); // h-20
                break;
            case 'banner':
                this._width.set(80); // w-20
                this._height.set(48); // h-12
                break;
            case 'header':
                this._width.set(72); // w-18
                this._height.set(48); // h-12
                break;
            default:
                this._width.set(72); // w-18
                this._height.set(50);
        }
    }

    private _convertFuseParrain(fuseParrain: any): Parrain {
        // Convertir le parrain de la config Fuse en objet Parrain
        return {
            logo: {
                id: fuseParrain.logo.id || '',
                url: fuseParrain.logo.url || '',
                urlExt: fuseParrain.logo.urlExt || '',
                extension: fuseParrain.logo.extension || 'png',
            },
            id: fuseParrain.id || '',
            name: fuseParrain.name || '',
            fullname: fuseParrain.fullname || '',
            country: fuseParrain.country || '',
            city: fuseParrain.city || '',
            street: fuseParrain.street || '',
            zipcode: fuseParrain.zipcode || '',
            phoneNumber: fuseParrain.phoneNumber || '',
            urlWebSite: fuseParrain.urlWebSite || '',
            isMainSponsor: fuseParrain.isMainSponsor || false,
            styles: fuseParrain.styles || {
                logo: {
                    size: {
                        sidebarLeftBottom: {
                            classnameWidth: 'w-20',
                            classnameHeight: 'h-auto',
                            width: 0,
                            height: 0,
                        },
                        bottomBarRight: {
                            classnameWidth: 'w-14',
                            classnameHeight: 'h-auto',
                        },
                        advertising: {
                            classnameWidth: 'w-32',
                            classnameHeight: 'h-auto',
                        },
                        bannerSide: {
                            classnameWidth: 'w-16',
                            classnameHeight: 'h-auto',
                        },
                        bannerFeed: {
                            classnameWidth: 'w-16',
                            classnameHeight: 'h-auto',
                        },
                        partnerPlace: {
                            classnameWidth: 'w-18',
                            classnameHeight: 'h-auto',
                        },
                    },
                },
            },
            customize: fuseParrain.customize || {
                needBackground: false, // Pas de background par défaut pour éviter les problèmes
                backgroundColor: 'bg-white',
                padding: 'p-1',
                borderRounded: true,
            },
        };
    }
}
